// @ts-check
/* global Tygh $ module require*/
'use strict';

declare var $;
declare var Tygh;

import { ChildConfigControls } from './components/child_config_controls';
import { PriceUpdater } from '../tv_configurator/components/price_updater';
import { ConfirmDialog } from '../tv_utils/confirm_dialog';
import { StickyCTA } from '../tv_utils/sticky_cta';
import type { ArtworkConfigurationModal } from './components/artwork_configuration_modal';
import { IParentConfiguration } from './types';
import type { ChildQuestionInput } from './components/child_question_input';
import { StateSelectorView } from '../tv_product_quotes/product_quotes';
import { init_g_recaptcha } from '../tv_third_party_integrations/g_recaptcha';
import type { OfflineConfiguration } from '../tv_admin/tv_configurator_v2/offline_configuration';
import type { DesignTool } from '../tv_designtool/designtool'
import { ProductPreview } from './components/product_preview';
import { QuestionModal } from './components/question_modal';

/**
 * Class to handle binding controls for the admin test configurator page.
 */
export class ConfiguratorV2 {
    /**
     * @var {HTMLSelectElement} parent_select
     * @var {ProductPageLoader} productPageLoader is used to load all controls associated with the product page.
     */
    artwork_configuration_modal: ArtworkConfigurationModal;
    artwork_configuration_modal_class: typeof ArtworkConfigurationModal;
    artwork_required: boolean;
    can_upload_artwork: boolean = true;
    child_configs: Array<ChildConfigControls> = [];
    child_elements: {[key: string]: {[key: number]: HTMLElement}} = {
        'id': {},
        'child_sku_id': {},
        'sku_id': {},
        'smart_code': {},
        'number_of_designs': {},
        'design_quantities': {},
        'print_detail_ids': {}
    };
    config_controls: HTMLCollectionOf<Element>;
    current_sku: object;
    design_tool: DesignTool;
    discount_group: string = 'TV_A';
    elements: {[key: string]: HTMLElement} = {};
    offline_configuration: OfflineConfiguration;
    parent_select: HTMLSelectElement;
    parent_child_relation_ids: Array<number>;
    parent_config: IParentConfiguration;
    pricing_data: {};
    price_per_piece: number;
    discounted_price_per_piece: number;
    price_updater: PriceUpdater;
    print_detail: object;
    product_id: number;
    purge_artwork: boolean;
    quantity: number = 1;
    questions: Map<number, number>;
    reconfig: boolean;
    sticky_cta: StickyCTA;
    total_price: number;
    discounted_total_price: number;
    company_id: number = 3;
    configuration_id: string;
    out_of_stock: boolean = false;
    save_reconfig_fn: () => void;
    bound_request_a_quote_fn: () => void;
    has_multiple_input_question: boolean = false;
    fetching_sku_data: { [key:number]: boolean } = {};
    sku_data_to_fetch: { [key:number]: boolean } = {};
    product_preview: ProductPreview;
    question_modals: Map<number, QuestionModal>;

    /**
     * Binds to the controls needed for the view.
     */
    constructor(reconfig, offline_configuration = null, save_reconfig_fn = null, design_tool = null) {
        this.reconfig = reconfig;
        this.offline_configuration = offline_configuration;
        this.design_tool = design_tool;
        this.save_reconfig_fn = this.save_reconfig.bind(this);
        if (save_reconfig_fn) {
            this.save_reconfig_fn = save_reconfig_fn;
        }

        const product_id = <HTMLInputElement>document.querySelector('[name="product_id"]');
        this.product_id = parseInt(product_id.value);
        const company_id = document.getElementById('company_id') as HTMLInputElement;
        this.company_id = parseInt(company_id.value);
        this.bind();

        const price_group = <HTMLInputElement>document.getElementById('price_group');
        this.discount_group = price_group.value;

        this.price_updater = new PriceUpdater(this.product_id, false, reconfig, true);

        if (this.elements.config_data) {
            this.parent_config = <IParentConfiguration>JSON.parse((<HTMLInputElement>this.elements.config_data).value);
            if (this.reconfig) {
                this.init_child_configs();
                this.update_pricing();
            }
        }

        // Only run video thumbnail script if there are video tabs
        const tv_tabs = Array.from(<HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ty-tabs__item'));
        const tv_mobile_tabs = Array.from(
            <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ui-accordion-header')
        );
        tv_tabs.forEach(tv_tab => {
            if (tv_tab.innerText == "Videos") {
                this.create_video_thumbnail();
            }
        });
        tv_mobile_tabs.forEach(tv_mobile_tab => {
            if (tv_mobile_tab.innerText == "Videos") {
                this.create_video_thumbnail();
            }
        });

        // For reconfigs, the artwork configuration modal is created here to facilitate access to the design quantity manager and modals.
        if (reconfig) {
            if (!save_reconfig_fn) {
                this.save_reconfig_fn = this.save_reconfig.bind(this);
            }
            if (this.artwork_required) {
                this.fetch_artwork_configuration_modal_class().then(() => {
                    this.artwork_configuration_modal = new this.artwork_configuration_modal_class(this, this.save_reconfig_fn);
                    // After initial construction & binding, check for any hardware only children that need removed from modal.
                    this.artwork_configuration_modal.update_order_configuration(this.get_print_item_parent_child_ids());
                    if (this.design_tool) {
                        // Reset artwork configuration modal.
                        this.artwork_configuration_modal.reset_steps();
                    }

                });
            }
        }

        let contains_print_item = this.contains_print_item();
        this.set_can_upload_artwork(contains_print_item);
        this.purge_artwork = false;

        // Allows number of design modal to work properly for the offline configurator.
        if (this.offline_configuration && this.quantity > 1) {
            this.purge_artwork = true;
        }

        // Create product preview if present.
        const product_preview_canvas = document.getElementById('tv-preview-canvas');
        if (product_preview_canvas) {
            this.product_preview = new ProductPreview(this);
        }
    }

    /**
     * Attach events and map references to the DOM.
     *
     * @returns {void}
     */
    bind() : void {
        const parent_child_relations = document.getElementsByClassName('js-parent-child-relation-id');
        this.parent_child_relation_ids = [];
        this.pricing_data = {};
        for (const parent_child_relation of parent_child_relations) {
            const parent_child_relation_id = parseInt((parent_child_relation as HTMLInputElement).value);
            this.parent_child_relation_ids.push(parent_child_relation_id);
            this.child_elements.id[parent_child_relation_id] =
                document.getElementById(`product_child_configuration_id-${parent_child_relation_id}`);
            this.child_elements.child_sku_id[parent_child_relation_id] =
                document.getElementById(`child_sku_id-${parent_child_relation_id}`);
            this.child_elements.sku_id[parent_child_relation_id] =
                document.getElementById(`sku_id-${parent_child_relation_id}`);
            this.child_elements.smart_code[parent_child_relation_id] =
                document.getElementById(`smart_code-${parent_child_relation_id}`);
            this.child_elements.print_detail_ids[parent_child_relation_id] =
                document.getElementById(`print_details-${parent_child_relation_id}`);
            this.child_elements.number_of_designs[parent_child_relation_id] =
                document.getElementById(`number_of_designs-${parent_child_relation_id}`);
            this.child_elements.design_quantities[parent_child_relation_id] =
                document.getElementById(`design_quantities-${parent_child_relation_id}`);
            this.pricing_data[parent_child_relation_id] = JSON.parse(
                (document.getElementById(`pricing-data-${parent_child_relation_id}`) as HTMLInputElement).value
            );
        }
        this.elements.config_data = document.getElementById('js-config-data');
        this.elements.total_price_display = document.getElementById('js-total-price');
        this.elements.product_details_heading = document.getElementById('js-product-details-heading');
        this.elements.config_block = document.getElementById('js-config-v2-block');
        this.elements.product_footer = document.getElementById('js-advanced-product-footer');
        this.elements.quote_window = document.getElementById('js-product-quote-window');

        const configuration_id = <HTMLInputElement>document.getElementById('session_id');
        this.configuration_id = configuration_id.value;

        this.bound_request_a_quote_fn = this.request_a_quote.bind(this);
        const request_a_quote_links = document.getElementsByClassName('js-configurator-request-a-quote');
        for (const request_a_quote_link of request_a_quote_links) {
            request_a_quote_link.removeEventListener('click', this.bound_request_a_quote_fn);
            request_a_quote_link.addEventListener('click', this.bound_request_a_quote_fn);
        }

        this.elements.quantity_max_value_alert = document.getElementById('js-quantity-max-value-alert');

        this.elements.quantity_input = <HTMLInputElement>document.getElementById('js-config-quantity');
        this.elements.quantity_input.addEventListener('change', this.update_quantity_input.bind(this), false);
        this.quantity = parseInt((<HTMLInputElement>this.elements.quantity_input).value);

        // Prevent implicit submission with Enter key on quantity input
        this.elements.quantity_input.addEventListener('keypress', (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                this.update_quantity_input(event);
            }
        });

        this.elements.save_reconfig_v2 = document.getElementById('js-save-reconfig-v2');
        if (this.elements.save_reconfig_v2) {
            this.elements.save_reconfig_v2.addEventListener('click', e => {
                e.preventDefault();
                if ((this.elements.quantity_input as HTMLInputElement).checkValidity()) {
                    this.confirm_save_reconfig();
                }
            });
        }

        this.elements.boxing_data = document.getElementById('js-shipping-estimate-boxing-data-input');
        this.elements.sku = document.getElementById(`product_code_${this.product_id}`);
        this.elements.template_download_button = document.getElementById('js-download-product-page-artwork-template-link');
        this.elements.template_download_modal_opener = document.getElementById('js-download-artwork-template-link-opener-link');
        this.elements.no_template = document.getElementById('js-no-template-upload-modal');
        this.artwork_required = (document.getElementById('artwork_not_required') as HTMLInputElement).value != '1';
        this.elements.free_proof_indicator = document.getElementById('js-free-proof-indicator');
        this.elements.js_design_options_modal = document.getElementById('js-artwork-configuration-modal');
        this.elements.js_proceed_to_artwork_button = document.getElementById('js-cart-artwork-configuration-modal-opener');
        this.elements.requested_sku_id = document.getElementById('requested_sku_id');
        this.elements.requested_child_sku_id = document.getElementById('requested_child_sku_id');

        // If this is a design options product, disable the CTA and footer until the data is populated.
        if (this.elements.js_design_options_modal
            && this.elements.js_design_options_modal.style.display != 'block'
        ) {
            if (this.elements.js_proceed_to_artwork_button) {
                this.elements.js_proceed_to_artwork_button.setAttribute('disabled', '');
                this.elements.js_proceed_to_artwork_button.classList.add('tv-button__disabled');
                this.elements.js_proceed_to_artwork_button.innerHTML = "Loading";
            }
            if (this.elements.template_download_button) {
                this.elements.template_download_button.classList.add('tv-display__none');
            }
        }

        if (!this.reconfig) {
            this.elements.add_to_cart_button = document.getElementById(`button_cart_${this.product_id}`);
            this.elements.artwork_get_started_buttons = document.getElementById('js-artwork-get-started-buttons');
            this.elements.disabled_out_of_stock_btn = document.getElementById('js-disabled-out-of-stock-btn');
        }

        var artwork_configuration_button = document.getElementById('js-cart-artwork-configuration-modal-opener');
        if (artwork_configuration_button) {
            artwork_configuration_button.addEventListener('click', (event) => {
                if (!(this.elements.quantity_input as HTMLInputElement).checkValidity()) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            });
        }

        // Quantity Break Table Controls.
        this.elements.js_see_quantity_discounts_table_btn = document.getElementById('js-see-quantity-discounts-table');
        this.elements.js_hide_quantity_discounts_table_btn = document.getElementById('js-hide-quantity-discounts-table');
        this.elements.js_quantity_discounts_table = document.getElementById('js-upper-qty-discounts-table');

        if (this.elements.js_see_quantity_discounts_table_btn && this.elements.js_hide_quantity_discounts_table_btn) {
            this.elements.js_see_quantity_discounts_table_btn.addEventListener('click', () => {
                this.elements.js_quantity_discounts_table.classList.remove('tv-display__none');
                this.elements.js_hide_quantity_discounts_table_btn.classList.remove('tv-display__none');
                this.elements.js_see_quantity_discounts_table_btn.classList.add('tv-display__none');
            });

            this.elements.js_hide_quantity_discounts_table_btn.addEventListener('click', () => {
                this.elements.js_quantity_discounts_table.classList.add('tv-display__none');
                this.elements.js_hide_quantity_discounts_table_btn.classList.add('tv-display__none');
                this.elements.js_see_quantity_discounts_table_btn.classList.remove('tv-display__none');
            });
        }

        // Step number buttons.
        const step_number_buttons = document.getElementsByClassName('js-step-number-input');

        for (const step_number_button of step_number_buttons) {
            step_number_button.addEventListener('click', (event) => {
                this.update_quantity_number_input(event)
            });
        }

        this.adjust_owl_wrapper();
        window.onresize = this.adjust_owl_wrapper;
        this.handle_canopy_links();
        this.handle_faq_section();

    }

    adjust_owl_wrapper() : void {
        let active_image;
        let owl_item;
        const owl_wrapper = document.querySelector('.ty-product-block__img-wrapper .owl-wrapper') as HTMLElement;
        const container_width = window.innerWidth - 30;
        const tablet_wrapper = document.querySelector('.ut2-pb__img-wrapper') as HTMLElement;

        if (owl_wrapper) {
            active_image = owl_wrapper.querySelector('img.cm-image') as HTMLElement;
            owl_item = owl_wrapper.querySelector('.active') as HTMLElement;
        }

        if (owl_wrapper && owl_item && tablet_wrapper && active_image) {
            // Mobile calculation
            owl_wrapper.style.minWidth = container_width + 'px';
            owl_item.style.minWidth = container_width + 'px';
            if (window.innerWidth > 767 && window.innerWidth < 1289) {
                // Tablet calculation
                owl_wrapper.style.minWidth = (tablet_wrapper.getBoundingClientRect().width - 25) + 'px';
                owl_item.style.minWidth = (tablet_wrapper.getBoundingClientRect().width - 25) + 'px';
            } else if (window.innerWidth > 1289) {
                // Desktop calculation
                const width = active_image.getAttribute('width') ? active_image.getAttribute('width') : active_image.getAttribute('height');
                owl_wrapper.style.minWidth = width + 'px';
                owl_item.style.minWidth = '';
            }

        }
    }

    update_quantity_number_input(event : Event) : void {
        const step_data = event.target.dataset;
        const number_input = <HTMLInputElement>this.elements.quantity_input;
        const step_type = step_data.stepType;
        const step_value = parseInt(step_data.stepValue, 10);

        // Determine if the adjustment value should be positive or negative based on the step type.
        const adjustment_value = (step_type === 'add') ? Math.abs(step_value) : -Math.abs(step_value);

        // Calculate the updated value with the current input value and the adjustment value.
        const updated_value = parseInt(number_input.value, 10) + adjustment_value;
        const min_quantity = parseInt(number_input.getAttribute('min'));
        const max_quantity = parseInt(number_input.getAttribute('max'));

        // Validate input ranges
        if (
            (!this.offline_configuration || this.offline_configuration.line_data.line_type !== 'Quotation')
            && (updated_value > max_quantity || updated_value < min_quantity)
        ) {
            let custom_message = Tygh.tr('tv_custom_validity_quantity_customer_service');
            custom_message = custom_message.replace(/{maxquantity}/g, max_quantity);
            custom_message = custom_message.replace(/{minquantity}/g, min_quantity);
            number_input.setCustomValidity(custom_message);
            number_input.reportValidity();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        number_input.setCustomValidity('');
        number_input.reportValidity();

        number_input.value = updated_value.toString();

        this.update_quantity_input(event);
    }

    update_quantity_input(e: Event) : void {
        const input = <HTMLInputElement>this.elements.quantity_input;
        const quantity = parseInt(input.value);

        const max_quantity = parseInt(input.max);
        const min_quantity = parseInt(input.min);

        if (!this.offline_configuration || this.offline_configuration.line_data.line_type !== 'Quotation') {
            if (this.elements.quantity_max_value_alert) {
                if (quantity > max_quantity) {
                    this.elements.js_proceed_to_artwork_button.setAttribute('disabled', '');
                    this.elements.js_proceed_to_artwork_button.classList.add('tv-button__disabled');
                    this.elements.quantity_max_value_alert.classList.remove('hidden');
                } else {
                    this.elements.js_proceed_to_artwork_button.removeAttribute('disabled');
                    this.elements.js_proceed_to_artwork_button.classList.remove('tv-button__disabled');
                    this.elements.quantity_max_value_alert.classList.add('hidden');
                }
            }

            if (quantity > max_quantity || quantity < min_quantity) {
                let custom_message = Tygh.tr('tv_custom_validity_quantity_customer_service');
                custom_message = custom_message.replace(/{maxquantity}/g, max_quantity);
                custom_message = custom_message.replace(/{minquantity}/g, min_quantity);
                input.setCustomValidity(custom_message);
                input.reportValidity();
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                return;
            }
        }

        input.setCustomValidity('');
        input.reportValidity();

        if (this.quantity === quantity) {
            return;
        }

        this.quantity = quantity;
        for (const child_config of Object.values(this.child_configs)) {
            child_config.default_question.quantity_changed(this.quantity);
        }

        // Get updated stock, production and boxing info and recreate artwork modal.
        this.fetch_sku_data();

        // For reconfigs, set purge artwork flag for quantity changes.
        if (this.reconfig) {
            this.purge_artwork = true;
        }

        // for offline configuration, update production time display
        if (this.offline_configuration) {
            this.offline_configuration.update_production_time();
        }

    }

    init_child_configs() : void {
        this.has_multiple_input_question = false;
        if (this.parent_config) {
            // Create QuestionModals separate from child configs to facilitate reuse.
            this.question_modals = new Map();
            const modal_openers = document.getElementsByClassName(`js-question-modal`) as HTMLCollectionOf<HTMLElement>;
            for(const modal_opener of modal_openers) {
                const question_id = parseInt(modal_opener.dataset.questionId);
                const question_modal = new QuestionModal(
                    question_id,
                    modal_opener.dataset.controlType
                )
                this.question_modals.set(question_id, question_modal);
            }
            // Initialize a ChildConfigurator for each child configuration.
            // Keep track of any propagating questions in order to set them at the end.
            let propagating_questions = new Map();
            for (const parent_child_relation of this.parent_config.parentChildRelations.reverse()) {
                // Pass in previous templates which may no longer exist on the page.
                // This occurs when loading a url of a sku that has fewer questions.
                let previous_child_config = this.child_configs[parent_child_relation.id];
                const previous_templates = previous_child_config?.child_questions;
                if (previous_child_config) {
                    previous_child_config.default_question.delete_all_next_questions();
                    previous_child_config.default_question.remove_event_listeners();
                }
                const child_config = new ChildConfigControls(
                    parent_child_relation,
                    this.child_question_updated.bind(this, parent_child_relation.id),
                    this.update_quantity_input.bind(this),
                    previous_templates,
                    !!this.offline_configuration, // coerce to bool
                    this.reconfig,
                    this.question_modals
                );
                if (child_config.has_multiple_input_question) {
                    this.has_multiple_input_question = true;
                }
                this.child_configs[parent_child_relation.id] = child_config;
                // Iterate over child config questions to find any propagators;
                let current_question = child_config.default_question;
                while (current_question) {
                    if (current_question.child_question_data.propagate) {
                        propagating_questions.set(current_question, parent_child_relation.id);
                    }
                    current_question = current_question.next_question;
                }
            }

            // If radio questions are being inserted within wall pickers, move them outside to prevent unwated css flex.
            const js_radio_questions = document.getElementsByClassName('js-radio-question');
            const radio_alt_display = document.getElementById('js-radio-alt-display');
            for (const radio_question of js_radio_questions) {
                if (!radio_question.classList.contains('hidden') && radio_question.parentElement.parentElement.classList.contains('js-wall-selection-item')) {
                    radio_alt_display.appendChild(radio_question);
                }
            }
            propagating_questions.forEach((value, key) => this.propagate_child_question_selection(value, key, true));

            const configurator_body = document.getElementById('js-config-v2-block');
            if (!this.reconfig || (configurator_body && configurator_body.dataset.location == "cart")) {
                this.sticky_cta = new StickyCTA();
            } else {
                this.child_configs_updated();
            }

            // Fetch stock, production and shipping info.
            this.fetch_sku_data();
        }
    }

    /**
     * Creates a thumbnail for users to click to go to the videos tab, if videos are present on this page
     *
     * @returns {void}
     */
     create_video_thumbnail(): void {

        // Create Video Thumbnail
        const product_thumbnail_container = document.getElementsByClassName('ty-product-thumbnails');
        const video_thumbnail_image = document.getElementsByClassName('youtube-player')[0]?.children[0]?.children[0]?.getAttribute('src');
        if (!video_thumbnail_image) {
            return;
        }
        const video_thumbnail = document.createElement("DIV");
        const video_play_icon = document.createElement("DIV");

        video_thumbnail.classList.add('ty-product-thumbnails__item');
        video_thumbnail.classList.add('tv-video-thumbnail');
        video_thumbnail.style.backgroundImage = 'url(' + video_thumbnail_image + ')';
        video_play_icon.classList.add('tv-video-thumbnail-play-btn');

        if (product_thumbnail_container.length) {
            const thumbnail_images = product_thumbnail_container[0].getElementsByTagName('img');
            if(thumbnail_images.length) {
                video_thumbnail.style.height = thumbnail_images[0].height + 'px';
                video_thumbnail.style.width = thumbnail_images[0].width + 'px';
            }
            product_thumbnail_container[0].appendChild(video_thumbnail);
        } else {
            return;
        }
        video_thumbnail.appendChild(video_play_icon);

        video_thumbnail.addEventListener('click', () => {
            this.go_to_video_thumbnail();
        });

    }

    /**
     * Function to scroll the user to the video thumbnail.
     * 
     * @returns {void}
     */
    go_to_video_thumbnail() : void {
        if (window.innerWidth >= 480) {
            const tv_tabs = Array.from(<HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ty-tabs__item'));
            tv_tabs.forEach(tv_tab => {
                if (tv_tab.innerText == 'Videos') {
                    tv_tab.click();
                    tv_tab.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                }
            });
        } else {
            const tv_mobile_tabs = Array.from(
                <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('ui-accordion-header')
            );
            tv_mobile_tabs.forEach(tv_mobile_tab => {
                if (tv_mobile_tab.innerText == 'Videos') {
                    tv_mobile_tab.click();
                    tv_mobile_tab.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                }
            });
        }
    }

    /**
     * Iterates over child configs and returns false if one has a print detail.
     *
     * @returns {boolean}
     */
    is_hardware_only() : boolean {
        for (const parent_child_relation_id in this.child_configs) {
            const child = this.child_configs[parent_child_relation_id];
            for (const contents of child.current_sku.skuContents) {
                if (contents.printDetailId > 0) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Sets configuration id and creates a new artwork configuration modal.
     *
     * @returns {void}
     */
    set_configuration_id(new_id: string) : void {
        this.configuration_id = new_id;

        if (!this.reconfig && this.artwork_required && this.contains_print_item()
                && !this.product_preview?.photo_upload_required) {
            this.fetch_artwork_configuration_modal_class().then(() =>{
                this.artwork_configuration_modal = new this.artwork_configuration_modal_class(this);
                // After initial construction & binding, check for any hardware only children that need removed from modal.
                this.artwork_configuration_modal.update_order_configuration(this.get_print_item_parent_child_ids());
            });
        }
    }

    /**
     * Updates data on page related to each Child Configuration as well as pricing and artwork configuration.
     *
     * @param {boolean} create_art_modal whether to create a new Artwork Configuration Modal.
     * @returns {void}
     */
    child_configs_updated(create_art_modal: boolean = false) : void {
        let child_sku_data_found = false;
        const smart_codes = {};
        const smart_codes_with_required_artwork = {};

        for (const parent_child_relation_id of Object.keys(this.child_configs).map((key) => parseInt(key))) {
            const child_sku_id = this.child_configs[parent_child_relation_id].child_sku_id;
            const parent_child_relation = this.parent_config.parentChildRelations.find(pcr => pcr.id == parent_child_relation_id);
            const child_sku_data = parent_child_relation.childConfiguration.childSKUs.find(cs => cs.id === child_sku_id);
            if (child_sku_data) {
                child_sku_data_found = true;
                const print_contents = child_sku_data.sku.skuContents.filter(sc => sc.printDetail);
                const child_smart_codes = {};
                const child_smart_codes_with_required_artwork = {};
                const child_print_detail_ids = [];
                for (const print_content of print_contents) {
                    const smart_code = child_sku_data.sku.parentSmartCode ?? print_content.printDetail.smartCode;
                    child_smart_codes[print_content.printDetailId] = smart_code;
                    if (print_content.printDetail.requiresArtwork) {
                        child_smart_codes_with_required_artwork[print_content.printDetailId] = smart_code;
                    }
                    child_print_detail_ids.push(print_content.printDetailId);
                }

                smart_codes[parent_child_relation_id] = child_smart_codes;
                smart_codes_with_required_artwork[parent_child_relation_id] = child_smart_codes_with_required_artwork;
                (this.child_elements.smart_code[parent_child_relation_id] as HTMLInputElement).value = JSON.stringify(child_smart_codes);
                (this.child_elements.child_sku_id[parent_child_relation_id] as HTMLInputElement).value =
                    child_sku_id.toString();
                (this.child_elements.sku_id[parent_child_relation_id] as HTMLInputElement).value = child_sku_data.sku.id.toString();
                (this.child_elements.print_detail_ids[parent_child_relation_id] as HTMLInputElement).value = JSON.stringify(child_print_detail_ids);
            }
        }

        // Check if item has changed to or from hardware only.
        const contains_print_item = this.contains_print_item();
        if (contains_print_item != this.can_upload_artwork) {
            this.set_can_upload_artwork(contains_print_item);
        }
        // For reconfigs, set purge artwork flag for any changes.
        if (this.reconfig) {
            this.purge_artwork = true;
        }

        // Update product preview in case template has changed.
        if (this.product_preview) {
            this.product_preview.reinitialize(smart_codes);
        }

        (async () => {
            // When fetching sku data, the art modal needs to be recreated after the smart codes are updated.
            if (create_art_modal && !this.reconfig && this.artwork_required && contains_print_item
                    && !this.product_preview?.photo_upload_required) {
                await this.fetch_artwork_configuration_modal_class();
                this.artwork_configuration_modal = new this.artwork_configuration_modal_class(this);
            }
        })().then(() => {
            if (child_sku_data_found) {
                this.update_pricing();
                if (this.artwork_configuration_modal) {
                    // If print item count has changed, artwork configuration modal updates the quantity, upload and difu managers.
                    this.artwork_configuration_modal.update_order_configuration(this.get_print_item_parent_child_ids(), this.quantity);
                    if (!this.reconfig) {
                        // Update template links in case smart code has changed.
                        this.update_artwork_template_links(smart_codes_with_required_artwork);
                    }
                }
            }
            if (this.elements.js_design_options_modal
                && this.elements.js_design_options_modal.style.display == 'block'
                && this.elements.js_proceed_to_artwork_button
            ) {
                this.elements.js_proceed_to_artwork_button.removeAttribute('disabled');
                this.elements.js_proceed_to_artwork_button.classList.remove('tv-button__disabled');
                this.elements.js_proceed_to_artwork_button.innerHTML = "Proceed to Artwork";
            }
        });
    }

    /**
     * Callback for when child question input is updated or added
     *
     * @returns {void}
     */
    child_question_updated(
        parent_child_relation_id: number,
        propagating_child_question: ChildQuestionInput,
        question_added: boolean,
        option_changed: boolean = true
    ): void {
        if (this.product_preview) {
            this.product_preview.update_canvas();
        }

        if (!option_changed) {
            return;
        }

        if (this.elements.save_reconfig_v2) {
            // Check if updated question selection should propagate to siblings and disable the save button while grabbing config data.
            this.elements.save_reconfig_v2.setAttribute('disabled', 'true');
            this.elements.save_reconfig_v2.classList.add('tv-button__disabled');
        }

        if (propagating_child_question) {
            this.propagate_child_question_selection(parent_child_relation_id, propagating_child_question, question_added);
        }

        if (!question_added) {
            // If this is a design options product, disable the CTA and footer until the data is populated.
            if (this.elements.js_proceed_to_artwork_button) {
                this.elements.js_proceed_to_artwork_button.setAttribute('disabled', '');
                this.elements.js_proceed_to_artwork_button.classList.add('tv-button__disabled');
                this.elements.js_proceed_to_artwork_button.innerHTML = "Loading";
            }
            if (this.elements.template_download_button) {
                this.elements.template_download_button.classList.add('tv-display__none');
            }
            this.fetch_sku_data(parent_child_relation_id);
        }

   }

   /**
     * Fetches SKU data using the current selections for each child.
     *
     * @returns {void}
     */
    fetch_sku_data(parent_child_relation_id: number = 0): void {
        if (parent_child_relation_id === 0) {
            parent_child_relation_id = this.parent_child_relation_ids[0];
        }

        if (this.fetching_sku_data[parent_child_relation_id]) {
            this.sku_data_to_fetch[parent_child_relation_id] = true;
            return;
        }
        this.fetching_sku_data[parent_child_relation_id] = true;

        const current_sku_selections = {};
        for (const parent_child_id in this.child_configs) {
            const child_config = this.child_configs[parent_child_id];
            const selections = {};
            child_config.current_selections.forEach(
                (value, key) => selections[key] = value
            );
            current_sku_selections[parent_child_id] = {
                'child_config_id': child_config.child_config.id,
                'selections': selections
            };
        }
        const input_data = {};
        const text_inputs = document.getElementsByClassName('js-text-input');
        for (const text_input of (text_inputs as HTMLCollectionOf<HTMLInputElement>)) {
            // Use the input name to mimic how data is passed as array via form.
            input_data[text_input.name.split(/\[(.*)/s)[1]] = text_input.value;
        }
        const custom_size_inputs = document.getElementsByClassName('js-custom-size-input');
        for (const custom_size_input of (custom_size_inputs as HTMLCollectionOf<HTMLInputElement>)) {
            input_data[custom_size_input.name.split(/\[(.*)/s)[1]] = custom_size_input.value;
        }
        const text_multiple_inputs = document.getElementsByClassName('js-text-multiple-input');
        for (const text_multiple_input of (text_multiple_inputs as HTMLCollectionOf<HTMLInputElement>)) {
            input_data[text_multiple_input.name.split(/\[(.*)/s)[1]] = text_multiple_input.value;
        }
        const text_quantity_inputs = document.getElementsByClassName('js-text-quantity-input');
        for (const text_quantity_input of (text_quantity_inputs as HTMLCollectionOf<HTMLInputElement>)) {
            input_data[text_quantity_input.name.split(/\[(.*)/s)[1]] = text_quantity_input.value;
        }
        const text_compound_multiple_inputs = document.getElementsByClassName('js-text-compound-multiple-input');
        for (const text_compound_multiple_input of (text_compound_multiple_inputs as HTMLCollectionOf<HTMLInputElement>)) {
            input_data[text_compound_multiple_input.name.split(/\[(.*)/s)[1]] = text_compound_multiple_input.value;
        }

        if (Object.keys(current_sku_selections).length) {
            const out_of_stock_text = <HTMLCollectionOf<HTMLInputElement>> document.getElementsByClassName('js-out-of-stock-text');
            const path = this.offline_configuration ? location.pathname.substring(1) : 'index.php';

            $.ceAjax('request', `${path}?dispatch=tv_configurator_v2.get_child_skus`, {
                method: 'POST',
                caching: true,
                hidden: true,
                result_ids: 'js-artwork-configuration-modal,js-download-artwork-template-link-modal',
                data: {
                    current_url: location.href,
                    product_id: this.product_id,
                    sku_selections: current_sku_selections,
                    discount_group: this.discount_group,
                    input_data: input_data,
                    quantity: this.quantity,
                    out_of_stock_text: out_of_stock_text[0]?.value,
                    offline_configuration: !!this.offline_configuration, // coerce to bool
                    session_id: this.configuration_id,
                    reconfig: this.reconfig
                },
                message: 'Initializing Product Configuration',
                callback: (response) => {
                    if (response.results) {
                        this.initialize_config_callback(response, parent_child_relation_id);
                        // Enable the proceed to artwork button and footer if the design options modal is populated.
                        if (this.elements.js_design_options_modal
                            && this.elements.js_proceed_to_artwork_button
                        ) {
                            this.elements.js_proceed_to_artwork_button.removeAttribute('disabled');
                            this.elements.js_proceed_to_artwork_button.classList.remove('tv-button__disabled');
                            this.elements.js_proceed_to_artwork_button.innerHTML = "Proceed to Artwork";
                        }
                    }
                }
            });
        }
    }

    initialize_config_callback(response, parent_child_relation_id: number): void {
        this.fetching_sku_data[parent_child_relation_id] = false;
        if (this.sku_data_to_fetch[parent_child_relation_id]) {
            this.sku_data_to_fetch[parent_child_relation_id] = false;
            this.fetch_sku_data(parent_child_relation_id);
            return;
        }

        // Update pricing data.
        this.pricing_data = response.results.pricing_data;

        // Set fetched skus to child configs.
        const child_config_skus = response.results.child_config_skus;
        for (const key in child_config_skus) {
            const child_sku = child_config_skus[key];
            this.child_configs[key].update_current_sku(child_sku);
            const parent_child_relation = this.parent_config.parentChildRelations.find(
                pcr => pcr.id == parseInt(key)
            );
            parent_child_relation.childConfiguration.childSKUs = [child_sku];
        }

        // Update max quantity based off of stock data.
        const quantity_input = this.elements.quantity_input as HTMLInputElement;
        let maximumQuantity = parseInt(quantity_input.dataset.configurationMaximumQuantity);
        if (!this.offline_configuration || this.offline_configuration.line_data.line_type !== 'Quotation') {
            if (response.results.stock_data.min_available >= 0) {
                maximumQuantity = Math.min(response.results.stock_data.min_available, maximumQuantity);
            }
            quantity_input.max = maximumQuantity.toString();
        }

        // Recreate artwork configuration modal.
        const create_art_modal = (parseInt(this.configuration_id) !== 0);
        // Update design quantity defaults.
        if ((this.has_multiple_input_question || this.offline_configuration) && !this.can_upload_artwork && this.contains_print_item()) {
            // Handle configurations with multiple input questions
            // This is not intended for use with multiple artwork designs and will require further testing
            // as it will reset all design quantities to the default when the quantity changes.
            // If this code is moved, please update the corresponding comment in ArtworkConfigurationModal.update_enabled_steps
            let design_index = 0;
            for (const parent_child_id of this.parent_child_relation_ids) {
                let designs = 0;
                let new_design_quantities = {};
                if (this.child_configs[parent_child_id].has_multiple_input_question) {
                    while (designs < this.quantity) {
                        new_design_quantities[design_index] = 1;
                        designs++;
                        design_index++;
                    }
                } else {
                    designs = 1;
                    // If we have a produced item but there is no artwork required, then this will be a stock or POD item.
                    // We need to set the design qty to the config qty so we get the correct qty for the print line
                    // when it comes into AX.
                    new_design_quantities[design_index] = this.quantity;
                    design_index++;
                }
                (this.child_elements.number_of_designs[parent_child_id] as HTMLInputElement).value = designs.toString();
                (this.child_elements.design_quantities[parent_child_id] as HTMLInputElement).value = JSON.stringify(new_design_quantities);
            }
        } else if (!this.reconfig || !this.can_upload_artwork) {
            let design_index = 0;
            for (const parent_child_id in response.results.sku_data) {
                const sku_data = response.results.sku_data[parent_child_id];
                let designs = '1';
                if (sku_data['producible_item_details']['artwork_required'] === false
                    && sku_data['producible_item_details']['produced_item'] === false
                ) {
                    designs = '0';
                    (this.child_elements.design_quantities[parent_child_id] as HTMLInputElement).value = '{}';
                } else {
                    // If we have a produced item but there is no artwork required, then this will be a stock or POD item.
                    // We need to set the design qty to the config qty so we get the correct qty for the print line
                    // when it comes into AX.
                    const qty = sku_data['producible_item_details']['artwork_required'] === false
                        || this.product_preview?.photo_upload_required ? this.quantity : 1;
                    (this.child_elements.design_quantities[parent_child_id] as HTMLInputElement).value = `{"${design_index}":${qty}}`;
                    design_index++;
                }
                (this.child_elements.number_of_designs[parent_child_id] as HTMLInputElement).value = designs;
            }
        }
        this.child_configs_updated(create_art_modal);

        // Modify the URL with the newly selected SKU(s), if we are on a product page.
        if (!this.offline_configuration && !this.reconfig) {
            // 5/30/2024 - Commenting this out to see if it's affecting SEO.
            // this.modify_url();
            if (this.elements.requested_sku_id) {
                const requested_sku_id = JSON.stringify(Object.values(child_config_skus).map(sku => sku.skuId));
                (this.elements.requested_sku_id as HTMLInputElement).value = requested_sku_id;
            }
            if (this.elements.requested_child_sku_id) {
                const child_sku_id = JSON.stringify(Object.values(child_config_skus).map(child_sku => child_sku.id));
                (this.elements.requested_child_sku_id as HTMLInputElement).value = child_sku_id;
            }
        }

        // Update stock, production and boxing data.
        let custom_message = this.offline_configuration
            ? Tygh.tr('tv_over_quantity_request_a_quote_offline')
            : Tygh.tr('tv_over_quantity_request_a_quote');
        custom_message = custom_message.replace(/{maxquantity}/g, maximumQuantity);
        custom_message = custom_message.replace(/{minquantity}/g, quantity_input.min);
        this.elements.quantity_max_value_alert.innerHTML = custom_message;
        // Decode entities
        this.elements.quantity_max_value_alert.innerHTML = this.elements.quantity_max_value_alert.innerText;

        const request_a_quote_links = document.getElementsByClassName('js-configurator-request-a-quote');
        for (const request_a_quote_link of request_a_quote_links) {
            request_a_quote_link.removeEventListener('click', this.bound_request_a_quote_fn);
            request_a_quote_link.addEventListener('click', this.bound_request_a_quote_fn);
        }

        this.out_of_stock = response.results.stock_data.out_of_stock
        const out_of_stock_notifications = document.getElementsByClassName('js-out-of-stock-notifications');
        for (const out_of_stock_notification_element of out_of_stock_notifications) {
            out_of_stock_notification_element.innerHTML = response.results.notifications;
        }

        if (this.offline_configuration && this.offline_configuration.line_data.line_type === 'Quotation') {
            this.out_of_stock = false;
        }

        this.show_add_to_cart_buttons();
        const production_times_input = <HTMLInputElement>document.getElementsByClassName('js-production-times')[0];
        if (production_times_input) {
            production_times_input.value = JSON.stringify(response.results.production_times);
        }

        if (!this.reconfig) {
            const delivery_estimator = document.getElementById('js-fastest-delivery');
            const product_amount_update = document.getElementById('product_amount_update_' + this.product_id);
            const config_summary = document.getElementById('config_summary');
            delivery_estimator.innerHTML = response.results.fastest_delivery_message;
            product_amount_update.innerHTML = response.results.product_amount;
            (this.elements.boxing_data as HTMLInputElement).value = response.results.boxing_data;
            config_summary.innerHTML = response.results.summary;
        }

        if (this.offline_configuration) {
            this.offline_configuration.update_sku_callback(parent_child_relation_id);
        }
    }

    /**
     * Propagates a child question's selection to any siblings with the same question.
     *
     * @returns {void}
     */
    propagate_child_question_selection(
        parent_child_relation_id: number,
        child_question: ChildQuestionInput,
        question_added: boolean
    ) : void {
        // The primary child question is the one that should be visible -
        // the first child config that has that particular question.
        // This should be the same as child_queston as that the only one that is visible
        // Unless question_added is true, in which case we need to set and hide child_question if they're different.
        let primary_child_question = null;
        const child_questions = {};

        for (const child_config_id in this.child_configs) {
            const child_config = this.child_configs[child_config_id];
            if (child_config.parent_child_relation.id === parent_child_relation_id) {
                if (!primary_child_question) {
                    primary_child_question = child_question;
                }
                child_questions[child_config_id] = child_question;
                continue;
            }

            // Iterate over child config questions until a match is found or the end is reached.
            let current_question = child_config.default_question;
            while (current_question) {
                if (current_question.question_id === child_question.question_id) {
                    // Only propagate to questions with propagate set
                    if (current_question.child_question_data.propagate) {
                        if (!primary_child_question) {
                            primary_child_question = current_question;
                        }
                        child_questions[child_config_id] = current_question;
                    }
                    break;
                }
                current_question = current_question.next_question;
            }
        }

        if (!primary_child_question) {
            primary_child_question = child_question;
        }

        if (child_question !== primary_child_question && !question_added) {
            // Only the primary question should be visible and therefore changeable,
            // but we handle this corner case anyway to be safe.
            primary_child_question.select_option(child_question.selected_option);
            // Make sure the primary element is visible
            primary_child_question.control_element_template.classList.remove('hidden');
            primary_child_question.control_element.classList.remove('hidden');

            for (const child_config of this.child_configs) {
                if (child_config.parent_child_relation.id === parent_child_relation_id) {
                    // Call the updated method manually to prevent cyclic propagation.
                    child_config.selections_updated(null, question_added, false);
                    break;
                }
            }
        }

        for (const child_config_id in child_questions) {
            const current_question = child_questions[child_config_id];
            if (current_question && current_question !== primary_child_question) {
                current_question.select_option(primary_child_question.selected_option);
                // Call the updated method manually to prevent cyclic propagation.
                this.child_configs[child_config_id].selections_updated(null, question_added, false);
                // Hide the control element.
                current_question.control_element_template.classList.add('hidden');
                current_question.control_element.classList.add('hidden');
            }
        }
    }

    update_pricing() {
        // Recalculate total price.
        let price_per_product = 0;
        let discounted_product_price = 0;
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            let base_price = 0;
            let base_discount_price = 0;
            if (!child_config.current_sku) {
                continue;
            }

            const break_prices = this.pricing_data[key][child_config.child_sku_id]['quantity_break_prices'];
            for (const break_key in break_prices) {
                const piece_price = break_prices[break_key];
                if (
                    this.quantity >= piece_price['minimum_quantity'] &&
                    this.quantity <= piece_price['maximum_quantity']
                ) {
                    base_price = piece_price['price'];
                    base_discount_price = piece_price['discounted_price'] ?? 0;
                    break;
                }
            }
            price_per_product += base_price;
            discounted_product_price += base_discount_price;
        }
        const discounted = discounted_product_price > 0;
        const discounted_total = discounted ? discounted_product_price * this.quantity : null;
        this.price_per_piece = price_per_product;
        this.total_price = price_per_product * this.quantity;
        this.discounted_price_per_piece = discounted ? discounted_product_price : price_per_product;
        this.discounted_total_price = this.discounted_price_per_piece * this.quantity;

        this.price_updater.update({
            amount_saved: discounted ? this.total_price - discounted_total : 0,
            discounted_price: discounted_total,
            discounted_piece: discounted ? discounted_product_price : null,
            list_price: 0,
            price_per_piece: price_per_product,
            quantity: this.quantity,
            total_price: this.total_price
        });

        const price_data = {
            total_price: this.total_price
        };
        (<HTMLInputElement>document.getElementById('js-config-price-data')).value = JSON.stringify(price_data);

        // Update sticky cta price on product page.
        if (this.sticky_cta) {
            this.sticky_cta.add_prices_to_display();
        }

        this.update_quantity_break_prices();
        this.update_sku();
    }

    /**
     * Update the quantity break prices table to reflect the current configuration selections.
     *
     * @returns {void}
     */
    update_quantity_break_prices(): void {
        const quantity_break_price_table = {};
        let unique_min_quantities = [];
        const child_config_break_prices = {};
        const max_quantity = (this.elements.quantity_input as HTMLInputElement).max;

        // Combine keys (min quantity) for all child configs.
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            const child_sku_break_prices = this.pricing_data[key][child_config.child_sku_id]['quantity_break_prices'];
            // For multi-child configs like tents, skip any children that don't have break price data.
            if (child_sku_break_prices.length == 0) {
                continue;
            }
            child_config_break_prices[key] = {};
            const child_min_quantities = [];
            for (const child_sku_key in child_sku_break_prices) {
                const min_quantity = child_sku_break_prices[child_sku_key]['minimum_quantity'];
                const break_price = child_sku_break_prices[child_sku_key]['price'];
                const discount_price = child_sku_break_prices[child_sku_key]['discounted_price'];
                child_config_break_prices[key][min_quantity] = discount_price ? discount_price : break_price;
                if (min_quantity <= max_quantity) {
                    child_min_quantities.push(min_quantity);
                }
            }
            unique_min_quantities = unique_min_quantities.concat(child_min_quantities);
        }
        unique_min_quantities.sort((a, b) => a - b);
        // Combine break prices at each min quantity.
        const cur_prices = {};
        for (const min_quantity of unique_min_quantities) {
            quantity_break_price_table[min_quantity] = 0;
            for (const key in child_config_break_prices) {
                const child_config_break_price = child_config_break_prices[key];
                if (child_config_break_price[min_quantity]) {
                    cur_prices[key] = child_config_break_price[min_quantity];
                }
                quantity_break_price_table[min_quantity] += cur_prices[key];
            }
        }

        // Create table elements from hidden model elements.
        const quantity_model_element = document.getElementById('js-quantity-break-table-head');

        // If no quantity modal element, leave function early.
        if (!quantity_model_element) {
            return;
        }

        const price_model_element = document.getElementById('js-quantity-break-table-data');
        const quantities_parent = quantity_model_element.parentElement;
        const prices_parent = price_model_element.parentElement;

        // Clear any previously cloned child elements.
        while (quantities_parent.childElementCount > 2) {
            quantities_parent.removeChild(quantities_parent.lastChild);
        }
        while (prices_parent.childElementCount > 2) {
            prices_parent.removeChild(prices_parent.lastChild);
        }

        const price_section = document.querySelector('.ty-qty-discount');
        const number_of_prices = Object.keys(quantity_break_price_table).length;

        const configurator_quantity_container = document.querySelector('[name="configurator-quantity-container"]');
        // If there is only one record don't do any of this.
        if (number_of_prices <= 1) {
            configurator_quantity_container.classList.add('tv-padding__top-20');
            price_section.classList.add('hidden');
        } else {
            configurator_quantity_container.classList.remove('tv-padding__top-20');
            price_section.classList.remove('hidden');
        }

        for (const key in quantity_break_price_table) {
            // Create quantity element.
            const quantity_element = quantity_model_element.cloneNode(true) as HTMLTableCellElement;
            quantity_element.innerText = key + "+";
            quantities_parent.appendChild(quantity_element);
            quantity_element.classList.remove("hidden");
            // Create price element.
            const price_element = price_model_element.cloneNode(true) as HTMLTableCellElement;
            if (window.innerWidth > 767) {
                (price_element.children[0] as HTMLElement).innerText = quantity_break_price_table[key].toFixed(2);
            } else {
                // Assign and unhide proper responsive table cells
                // On initial load, the table may not have changed to mobile format yet.
                if (price_element.children.length < 2) {
                    break;
                }
                (price_element.children[0] as HTMLElement).innerText = key + '+';
                (price_element.children[1] as HTMLElement).innerText = "$" + quantity_break_price_table[key].toFixed(2);
                const quantity_headers = document.getElementsByClassName('ty-qty-discount__td');
                for (const quantity_header of quantity_headers) {
                    if (quantity_header.children[0]) {
                        quantity_header.children[0].classList.remove('tv-display__none');
                    }
                }
            }
            prices_parent.appendChild(price_element);
            price_element.classList.remove("hidden");
        }
    }

    /**
     * Update the sku to reflect the current configuration selections.
     *
     * @returns {void}
     */
    update_sku(): void {
        let sku_display = '';
        let sku_free_shipping = true;
        let is_development = (this.elements.sku
             && this.elements.sku.getAttribute('environment') === 'development');
        const current_sku_ids = [];
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            let child_sku_display = '';
            if (child_config.current_sku) {
                current_sku_ids.push(child_config.current_sku.id);
                child_sku_display = child_config.current_sku.skuName;
                if (is_development) {
                    child_sku_display += ` (${child_config.current_sku.id})`;
                }
            }
            sku_display = sku_display ? sku_display + " / " + child_sku_display :
                child_sku_display;
            if (!child_config.current_sku.freeShipping) {
                sku_free_shipping = false;
            }
        }
        if (this.elements.sku) {
            this.elements.sku.innerText = sku_display;
        }
        if (this.elements.boxing_data) {
            let boxing_data = JSON.parse(this.elements.boxing_data.value);
            boxing_data.products[this.product_id].sku_free_shipping = sku_free_shipping;
            this.elements.boxing_data.value = JSON.stringify(boxing_data);

        }
        if (!this.reconfig && !this.offline_configuration) {
            document.getElementById('js-breadcrumb-sku-display').innerText = "SKU: " + sku_display;
        }
    }

    modify_url(): void {
        // Get the path name "/blade-flags"
        const pathName = window.location.pathname;
        // Split it on the "/", this will result in an array where the key 1 is always the dispatch "blade-flags"
        const pathArray = pathName.split('/');
        // Rebuild the URL for the newly selected SKU(s) - "/"" + "blade-flags" + "/" + "SKU00001" + "/" + "SKU00002"
        let nextUrl = "/" + pathArray[1];
        this.child_configs.forEach((child_config) => nextUrl += `/${child_config.current_sku.skuName}`);

        // Replace the current state with the newly built path "/blade-flags/SKU00001/SKU00002"
        window.history.replaceState('data', '', nextUrl);
    }

     /**
     * Updates the can_upload_artwork flag
     *
     * @returns {void}
     */
      set_can_upload_artwork(contains_print_item: boolean): void {
        this.can_upload_artwork = this.artwork_required && contains_print_item;
        this.show_add_to_cart_buttons();
      }

     /**
     * Hides or displays artwork and add to cart buttons on product page.
     *
     * @returns {void}
     */
      show_add_to_cart_buttons(): void {
        if (this.can_upload_artwork && (!this.product_preview || !this.product_preview.photo)) {
            this.elements.add_to_cart_button?.classList.add('tv-display__none');
            this.elements.artwork_get_started_buttons?.classList.remove('tv-display__none');
            this.elements.free_proof_indicator?.classList.remove('tv-display__none');
            this.elements.disabled_out_of_stock_btn?.classList.add('tv-display__none');
        }
        else {
            this.elements.add_to_cart_button?.classList.remove('tv-display__none');
            this.elements.artwork_get_started_buttons?.classList.add('tv-display__none');
            this.elements.free_proof_indicator?.classList.add('tv-display__none');
            this.elements.disabled_out_of_stock_btn?.classList.add('tv-display__none');
        }

        const summary_add_to_cart_buttons = document.getElementsByClassName('js-configurator-summary-add-to-cart');
        const save_offline_configuration_button = document.getElementById('js-save-configuration');
        if (this.out_of_stock) {
            this.elements.add_to_cart_button?.classList.add('tv-display__none');
            this.elements.artwork_get_started_buttons?.classList.add('tv-display__none');
            this.elements.disabled_out_of_stock_btn?.classList.remove('tv-display__none');
            this.elements.save_reconfig_v2?.classList.add('tv-display__none');
            save_offline_configuration_button?.classList.add('tv-display__none');

            for (const button of summary_add_to_cart_buttons) {
                button.classList.add('tv-display__none');
            }
        } else {
            this.elements.save_reconfig_v2?.classList.remove('tv-display__none');
            save_offline_configuration_button?.classList.remove('tv-display__none');
            for (const button of summary_add_to_cart_buttons) {
                button.classList.remove('tv-display__none');
            }
        }

        if (this.elements.save_reconfig_v2) {
            // Enable the save button after everything is loaded.
            this.elements.save_reconfig_v2.removeAttribute('disabled');
            this.elements.save_reconfig_v2.classList.remove('tv-button__disabled');
        }
    }

    /**
     * Iterates over a each child config's sku contents and returns the
     * parent child relation ids of any children with print items.
     *
     * @returns {Array<number>} containing the parent child relation ids of any children with print items.
     */
    get_print_item_parent_child_ids(): Array<number> {
        let parent_child_ids = new Array<number>();
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            if (this.child_contains_print_item(child_config)) {
                parent_child_ids.push(child_config.parent_child_relation.id);
            }
        }
        return parent_child_ids;
    }

    /**
     * Iterates over a each child config's sku contents and checks for a print detail.
     *
     * @returns {boolean} true if a print item is found.
     */
    contains_print_item(): boolean {
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            if (this.child_contains_print_item(child_config)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Iterates over a child config's sku contents and checks for a print detail.
     *
     * @param {ChildConfigControls} child_config the child config to check.
     * @returns {boolean} true if a print item is found.
     */
    child_contains_print_item(child_config: ChildConfigControls): boolean {
        for (const sku_content of child_config.current_sku.skuContents) {
            if (sku_content.printDetail) {
                return true;
            }
        }
        return false;
    }

    /**
     * Iterates over a child config's sku contents and checks for required artwork.
     *
     * @param {number} parent_child_id the parent child relation id of the child config to check.
     * @returns {boolean} true if a print item with required artwork is found.
     */
    child_requires_artwork(parent_child_id: number): boolean {
        const child_config = this.child_configs[parent_child_id];
        for (const sku_content of child_config.current_sku.skuContents) {
            if (sku_content.printDetail && sku_content.printDetail.requiresArtwork) {
                return true;
            }
        }
        return false;
    }

    /**
     * Handles the faux links within the canopy pages without actually refreshing or altering the page URL.
     *
     * @returns {void}
     */
     handle_canopy_links(): void {
        const js_specified_canopy_link = document.getElementById('js-specified-canopy-link');
        const js_specified_canopy_image = document.getElementById('js-specified-canopy-image');
        if (js_specified_canopy_link) {
            js_specified_canopy_link.addEventListener('click', ()=> {
                js_specified_canopy_image.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            });
        }
    }

    /**
     * Handles behavior of faq section such as adding accordion function when mobile.
     *
     * @returns {void}
     */
     handle_faq_section(): void {
        const faq_section = document.getElementById('faq-section');

        if (faq_section && window.innerWidth) {
            const faq_headers = faq_section.querySelectorAll('p.tv-font-style__bold');
            for (const faq_header of faq_headers) {
                const faq_content = faq_header.nextElementSibling;

                // Hide sections by default.
                faq_content.classList.add('tv-display__none');
                faq_header.addEventListener('click', () => {
                    if (faq_content.classList.contains('tv-display__none')) {
                        faq_content.classList.remove('tv-display__none');
                    } else {
                        faq_content.classList.add('tv-display__none');
                    }
                });
            }
        }
     }

    /**
     * Update the config summary modal to reflect the current configuration selections.
     *
     * @returns {void}
     */
     show_summary(e: Event): void {
        const config_summary = document.getElementById('config_summary').getElementsByTagName('tbody')[0];
        this.update_summary(config_summary);
     }

    /**
     * Updates a config summary to reflect the current configuration selections.
     * Used for both the config summary modal and the product added summary.
     *
     * @returns {void}
     */
    update_summary(config_summary: HTMLElement): void {
        // Update prices.
        const summary_prices_per_piece = document.getElementsByClassName('js-summary-price-per-piece');
        for (const summary_price_per_piece of summary_prices_per_piece) {
            (summary_price_per_piece as HTMLElement).innerText = this.discounted_price_per_piece.toFixed(2);
        }
        const summary_total_prices = document.getElementsByClassName('js-summary-total-price');
        for (const summary_total_price of summary_total_prices) {
            (summary_total_price as HTMLElement).innerText = this.discounted_total_price.toFixed(2);
        }
    }

    /**
     * Helper function for filling in summary cell data appropriate to current company.
     *
     */
    set_summary_cell_data(parent_row: HTMLTableRowElement, column: number, company_details: Array<any>) : HTMLTableCellElement {
        const data_cell = parent_row.children[column] as HTMLTableCellElement;
        const matching_details = company_details.find(
            entry => (entry.companyId === this.company_id)
        ) ?? company_details[0];
        data_cell.innerText = matching_details.name ? matching_details.name : company_details[0].name;
        return data_cell;
    }

    /**
     * Checks if child config for specific parent child relationship has multiple print details.
     *
     * @returns {boolean} true if there are multiple print details.
     */
    has_multi_print_detail_child(parent_child_relation_id: number): boolean {
        let num_print_details = 0;
        const current_sku = this.child_configs[parent_child_relation_id].current_sku;
        if (!current_sku) {
            return false;
        }

        for (const sku_content of current_sku.skuContents) {
            if (sku_content.printDetail) {
                num_print_details += 1;
            }
        }

        return num_print_details > 1;
    }

    /**
     * Displays a confirmation dialog when saving a reconfig, if current artwork will be purged.
     *
     * @returns {void}
     */
    confirm_save_reconfig(): void {
        // Prevent confirmation dialog for product preview reconfigs.
        const product_preview_element = document.getElementById('product_preview') as HTMLInputElement;
        const is_product_preview = product_preview_element && product_preview_element.value !== 'none';
        // Provide a warning to the user about possibly deleting artwork.
        if (this.can_upload_artwork && this.purge_artwork && !is_product_preview) {
            // Skip warning for offline configurations.
            if (this.offline_configuration) {
                if (this.quantity > 1 && !this.has_multiple_artwork_for_config()) {
                    (document.getElementById(`js-multiple-design-question-opener`) as HTMLAnchorElement).click();
                } else {
                    this.save_reconfig_fn();
                }
                return;
            }
            let confirm_dialog_message = 'tv_cart_artwork_delete_warning';
            // Skip warning in design tool if it has/had a single design.
            if (this.design_tool) {
                const initial_number_of_designs = parseInt((document.getElementById(
                    `js-initial-number-of-designs`) as HTMLInputElement).value, 10);
                let number_of_designs = 0;
                for (const key in this.child_configs) {
                    number_of_designs += parseInt(
                        (this.child_elements.number_of_designs[key] as HTMLInputElement).value,
                        10
                    );
                }
                confirm_dialog_message = 'tv_designtool_artwork_delete_warning';
                if (initial_number_of_designs === 1 && number_of_designs === 1 && this.quantity === 1) {
                    this.save_reconfig_fn();
                    return;
                }
            }
            const confirm_dialog =
            new ConfirmDialog(
                () => {
                    if (this.can_upload_artwork && this.quantity > 1 && !this.has_multiple_artwork_for_config()) {
                        this.show_design_quantity_modal();
                    } else {
                        this.save_reconfig_fn();
                    }
                },
                null,
                confirm_dialog_message,
                true
            );
        } else {
            this.save_reconfig_fn();
        }
    }

    /**
     * Checks to see if configuration contains multiple lines that require artwork.
     *
     * @returns {boolean}
     */
    has_multiple_artwork_for_config(): boolean {
        // If there is only one child, check to make sure it doesn't have multiple print details.
        if (this.parent_child_relation_ids.length === 1) {
            return this.has_multi_print_detail_child(this.parent_child_relation_ids[0]);
        }

        // If there are multiple children, check to see how many require artwork.
        let children_requiring_artwork = 0;
        for (const parent_child_id of this.parent_child_relation_ids) {
            if (this.child_requires_artwork(parent_child_id)) {
                children_requiring_artwork += 1;
            }
        }

        return children_requiring_artwork > 1;
    }

    /**
     * Displays the design quantity modal when saving a reconfig, when current artwork will be purged.
     *
     * @returns {void}
     */
    show_design_quantity_modal(): void {
        // Design quantity modal must be opened after the confirmation dialog closes.
        // It's visible state must be tracked so we can raise it above any overlays
        const confirmation_modal = document.getElementById('js-confirmation-modal');
        const artwork_configuration_modal = document.getElementById('js-artwork-configuration-modal');
        const config = { attributes: true }
        const artwork_configuration_observer = new MutationObserver(
            (mutationsList, observer) => {
                if (getComputedStyle(artwork_configuration_modal.parentElement).display != 'none') {
                    artwork_configuration_modal.classList.add('visible')
                } else if (artwork_configuration_modal.classList.contains('visible')) {
                    artwork_configuration_modal.classList.remove('visible');
                    observer.disconnect();
                }
            }
        );
        const confirmation_observer = new MutationObserver(
            (mutationsList, observer) => {
                (document.getElementById(`js-multiple-design-question-opener`) as HTMLAnchorElement).click();
                 observer.disconnect();
            }
        );
        artwork_configuration_observer.observe(artwork_configuration_modal.parentElement, config);
        confirmation_observer.observe(confirmation_modal.parentElement, config);
    }

    /**
     * Gets reconfiguration data from the reconfig modal.
     *
     * @returns {Object} containing the quantity, configuration id, and child configuration data.
     */
    get_reconfig_data(): {} {
        const child_configurations = {};
        for (const parent_child_relation_id of this.parent_child_relation_ids) {
            child_configurations[parent_child_relation_id] = {
                id: (this.child_elements.id[parent_child_relation_id] as HTMLInputElement).value,
                sku_id: (this.child_elements.sku_id[parent_child_relation_id] as HTMLInputElement).value,
                child_sku_id: (this.child_elements.child_sku_id[parent_child_relation_id] as HTMLInputElement).value,
                smart_code: (this.child_elements.smart_code[parent_child_relation_id] as HTMLInputElement).value,
                number_of_designs: (this.child_elements.number_of_designs[parent_child_relation_id] as HTMLInputElement).value,
                design_quantities: (this.child_elements.design_quantities[parent_child_relation_id] as HTMLInputElement).value,
                questions: []
            }
            // Check for input data.
            let question_input = this.child_configs[parent_child_relation_id].default_question;
            while (question_input) {
                if (question_input.control_type === 'Input') {
                    child_configurations[parent_child_relation_id]['questions'][question_input.question_id] = {
                        input: question_input.input_elements[0].value
                    };
                } else if (question_input.control_type === 'Custom Size Input') {
                    const size_inputs = question_input.control_element.getElementsByClassName('js-custom-size-input');
                    child_configurations[parent_child_relation_id]['questions'][question_input.question_id] = {
                        input: [
                            (size_inputs[0] as HTMLInputElement).value,
                            (size_inputs[1] as HTMLInputElement).value
                        ]
                    };
                } else if (question_input.control_type === 'Multiple Input') {
                    child_configurations[parent_child_relation_id]['questions'][question_input.question_id] = {
                        input: JSON.stringify(question_input.input_elements.map(e => e.value.toString()))
                    };
                } else if (question_input.control_type === 'Compound Multiple Input') {
                    let compound_input = [];
                    for (let i = 0; i < question_input.input_elements.length; i++) {
                        compound_input.push([
                            question_input.input_elements[i].value,
                            question_input.input_elements[++i].value
                        ]);
                    }
                    child_configurations[parent_child_relation_id]['questions'][question_input.question_id] = {
                        compound_multiple_input: compound_input
                    };
                } else if (question_input.control_type === 'Quantity-Option1') {
                    child_configurations[parent_child_relation_id]['questions'][question_input.question_id] = {
                        input: `{${question_input.input_elements.map(
                            e => `"${e.dataset.optionName}": ${e.value}`
                        ).join(',')}}`
                    };
                }
                question_input = question_input.next_question;
            }
        }
        // Prevent artwork deletion for product preview reconfigs.
        const product_preview_element = document.getElementById('product_preview') as HTMLInputElement;
        const is_product_preview = product_preview_element && product_preview_element.value !== 'none';
        return  {
            quantity: this.quantity,
            configuration_id: this.configuration_id,
            child_configurations: child_configurations,
            delete_artwork: is_product_preview ? '0' : '1'
        };
    }

    /**
     * Sends request to save the reconfiguration and reload the cart page.
     *
     * @returns {void}
     */
    save_reconfig(): void {
        const data = this.get_reconfig_data();

        $.ceAjax('request', 'index.php?dispatch=checkout.save_reconfig_v2', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Saving Changes',
            'callback': () => {
                // Reload cart page clearing cache.
                window.location.assign('index.php?dispatch=checkout.cart');
                setTimeout(() => {
                    $.toggleStatusBox('show', {
                        overlay: true,
                        statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Updating Cart</span>',
                        statusClass: 'ty-ajax-loading-box_text_block'
                    });
                }, 100);
            }
        });

    }

    /**
     * Updates the template links in modal on the product page
     * @param smart_codes - optional, indexed by parent child relation
     *
     * @returns {void}
     */
    update_artwork_template_links(smart_codes? : {[key: number]: {[key: number]: string}}) : void {
        const base_url = window.location.origin;
        const dispatch = 'index.php?dispatch=tv_artwork.get_template';
        const mode = 'download';

        const unique_smart_codes = this.get_unique_smart_codes(smart_codes);
        const urls : {[key: number]: {[key: number]: string}} = {};
        let template_count = 0;
        for (const parent_child_relation_id in unique_smart_codes) {
            for (const print_detail_id in unique_smart_codes[parent_child_relation_id]) {
                const child_smart_code = unique_smart_codes[parent_child_relation_id][print_detail_id];
                if (!urls[parent_child_relation_id]) {
                    urls[parent_child_relation_id] = {};
                }
                urls[parent_child_relation_id][print_detail_id] = `${base_url}/${dispatch}&smart_code=${child_smart_code}&branded=0&mode=${mode}`;
                template_count++;
            }
        }

        const template_enabled = !this.elements.no_template || (<HTMLInputElement>this.elements.no_template).value !== '1';
        if (this.elements.template_download_button) {
            if (template_count === 1 && template_enabled) {
                (<HTMLAnchorElement>this.elements.template_download_button).href = Object.values(Object.values(urls)[0])[0];
                this.elements.template_download_button.classList.remove('tv-display__none');
            } else {
                this.elements.template_download_button.classList.add('tv-display__none');
            }
        }
        if (this.elements.template_download_modal_opener) {
            if (template_count > 1 && template_enabled) {
                this.elements.template_download_modal_opener.classList.remove('tv-display__none');
            } else {
                this.elements.template_download_modal_opener.classList.add('tv-display__none');
            }
        }
    }

    /**
     * Updates the friendly names in the artwork configuration modal
     *
     * @returns {void}
     */
    update_artwork_template_friendly_names() : void {
        for (const parent_child_relation_id of this.parent_child_relation_ids) {
            // Collect all friendly names in array, combine into one name for combined elements.
            const friendly_name_elements = document.getElementsByClassName(`js-product-page-friendly-name-${parent_child_relation_id}`);
            let combined_friendly_name = '';
            for (const sku_content of this.child_configs[parent_child_relation_id].current_sku.skuContents) {
                if (sku_content.printDetail) {
                    if (combined_friendly_name.length > 0) {
                        combined_friendly_name += '/'
                    }
                    const friendly_name = sku_content.printDetail.friendlyName;
                    combined_friendly_name += friendly_name;
                    // Set friendly name for specific print detail.
                    const single_friendly_name_elements = document.getElementsByClassName(
                        `js-product-page-friendly-name-${parent_child_relation_id}-${sku_content.printDetailId}`
                    );
                    for (const single_friendly_name_element of single_friendly_name_elements) {
                        // If the print detail requires artwork, add the friendly name. Otherwise hide it.
                        if (sku_content.printDetail.requiresArtwork) {
                            (single_friendly_name_element as HTMLElement).innerHTML += friendly_name;
                        } else {
                            (single_friendly_name_element as HTMLElement).parentElement.parentElement.classList.add('tv-display__none');
                        }
                    }
                    if (this.child_configs[parent_child_relation_id].current_sku.hasSelectablePrints) {
                        combined_friendly_name = combined_friendly_name.replace(/\| Small|\| Medium|\| Large|\| XL|\| 2XL|\| 3XL/gi, '');
                        break;
                    }
                }
            }
            for (const friendly_name_element of friendly_name_elements) {
                (friendly_name_element as HTMLElement).innerHTML = combined_friendly_name;
            }
        }
    }

    /**
     * Returns a set of unique, non-empty smart codes.
     * If the smart codes aren't provided, this will fetch them from the page
     * @param smart_codes by parent child relation id
     * @returns unique, non-empty smart codes indexed by parent child relation id and print detail id.
     */

    public get_unique_smart_codes(smart_codes? : {[key: number]: {[key: number]: string}}) : {[key: number]: {[key: number]: string}} {
        const smart_codes_rev = {};
        const unique_smart_codes = {};
        if (typeof smart_codes === 'undefined') {
            for (const parent_child_relation_id of this.parent_child_relation_ids) {
                const smart_code_element =
                    <HTMLInputElement>document.getElementById('smart_code-' + parent_child_relation_id);

                if (!smart_code_element.value) {
                    continue;
                }
                const child_smart_codes = JSON.parse(smart_code_element.value);
                for (const print_detail_id in child_smart_codes) {
                    const smart_code  = child_smart_codes[print_detail_id];
                    if (!smart_codes_rev[smart_code]) {
                        smart_codes_rev[smart_code] = parent_child_relation_id;
                        if (!unique_smart_codes[parent_child_relation_id]) {
                            unique_smart_codes[parent_child_relation_id] = {};
                        }
                        unique_smart_codes[parent_child_relation_id][print_detail_id] = smart_code;
                    }
                }
            }
        } else {
            for (const parent_child_relation_id in smart_codes) {
                const child_smart_codes = smart_codes[parent_child_relation_id];
                for (const print_detail_id in child_smart_codes) {
                    const smart_code  = child_smart_codes[print_detail_id];
                    if (!smart_codes_rev[smart_code]) {
                        smart_codes_rev[smart_code] = parent_child_relation_id;
                        if (!unique_smart_codes[parent_child_relation_id]) {
                            unique_smart_codes[parent_child_relation_id] = {};
                        }
                        unique_smart_codes[parent_child_relation_id][print_detail_id] = smart_code;
                    }
                }
            }
        }
        return unique_smart_codes;
    }


    /**
     * Replace the configuration window with the request a quote screen.
     *
     * @listens click
     * @returns {void}
     */
     request_a_quote(): void {
        // Hide the Header, Footer, and Configurator Window.
        this.elements.product_details_heading.classList.add('hidden');
        this.elements.config_block.classList.add('hidden');
        this.elements.product_footer.classList.add('hidden');

        const current_child_sku_ids = {};
        const current_sku_ids = {};
        for (const key in this.child_configs) {
            const child_config = this.child_configs[key];
            if (child_config.current_sku) {
                current_child_sku_ids[child_config.id] = child_config.child_sku_id;
                current_sku_ids[child_config.id]=child_config.current_sku.id;
            }
        }


        const data = {
            'results_id': 'js-product-quote-window',
            'session_id': this.configuration_id,
            'product_id': this.product_id,
            'quantity': this.elements.quantity_input.value,
            'child_skus': current_child_sku_ids,
            'skus': current_sku_ids,
            'configurator_v2': true
        };

        // Populate the quote div.
        $.ceAjax('request', 'index.php?dispatch=tv_product_quotes.load_request_a_quote_window', {
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': data,
            'message': 'Loading Quote Information',
            'callback': () => {
                (() => new StateSelectorView())();

                this.elements.return_to_configurator = document.getElementById('js-return-to-configuration');
                this.elements.return_to_configurator.addEventListener('click', this.return_to_configurator.bind(this));

                // Scroll to the quote div.
                const quote_window = document.getElementById('js-product-quote-window');
                quote_window.scrollIntoView();

                init_g_recaptcha();
            }
        });
    }

    /**
     * Replaces the request a quote screen with the configuration window.
     *
     * @listens click
     * @returns {void}
     */
     return_to_configurator(): void {
        // Show the Header, Footer, and Configurator window.
        this.elements.product_details_heading.classList.remove('hidden');
        this.elements.config_block.classList.remove('hidden');
        this.elements.product_footer.classList.remove('hidden');

        // Wipe out the quote screen
        this.elements.quote_window.innerHTML = '';
    }

    /**
     * Dynamically loads artwork configuration modal class if needed
     */
    async fetch_artwork_configuration_modal_class() : Promise<void> {
        if (!this.artwork_configuration_modal_class) {
            this.artwork_configuration_modal_class = (await import('./components/artwork_configuration_modal')).ArtworkConfigurationModal;
        }
    }

}

